import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, breadSlice, stickLegs, checkNpcClick } from '../entities/townsfolk.js';
import { HUD } from '../ui/hud.js';
import { setFlag, getFlag, trainStat, addCrumbs } from '../core/save.js';
import { questsForGiver, acceptQuest, turnIn, isReady as isQuestReady, objectiveProgress, QUESTS, progress as questProgress } from '../core/quests.js';

// The Avocado Rec Center — the indoor half of the court building. Bread Boi
// roams a gym floor of workout equipment (walk up to use it, flavor only for
// now) with a wall reserved for FUTURE SPORTS (tennis / soccer / boxing / pool)
// that will grow into their own minigames. Exit heads back out to the court.

export function createRecCenterScene(){
  return () => new RecCenterScene();
}

class RecCenterScene {
  constructor(){
    this.poseT = 0;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xD8E9D2);        // soft gym-green haze

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 11;
    this.camTarget = new THREE.Vector3(0, 1.6, 0);
    this.azimuth = 0; this.polar = 0.45;

    this._buildRoom();
    this._buildEquipment();
    this._buildFutureWall();
    this.coachTab = 'quests';

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 3.2);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.coach = this._makeCoach();
    this.scene.add(this.coach.root);

    this.panels = [];

    this.workouts = {
      treadmill: { title: 'Treadmill Sprint', stat: 'agi', duration: 5, clicks: 8, pose: 'run',    msg: 'cardio crushed! agility up.' },
      dumbbells: { title: 'Dumbbell Curls',  stat: 'str', duration: 5, clicks: 8, pose: 'lift',   msg: 'toast is pumped! strength up.' },
      bench:     { title: 'Bench Press',     stat: 'str', duration: 5, clicks: 8, pose: 'lift',   msg: 'bar crushed! strength up.' },
      bag:       { title: 'Heavy Bag',       stat: 'str', duration: 5, clicks: 6, pose: 'lift',   msg: 'one-two crunch! strength up.' },
      yoga:      { title: 'Yoga Flow',       stat: 'zen', duration: 4, clicks: 6, pose: 'meditate', msg: 'so zen. inner crumb balanced.' }
    };

    this.hotspots = [
      { pos: new THREE.Vector3(-4.5, 0, -2), r: 2.0, label: '🏃 hop on the treadmill', workout: 'treadmill' },
      { pos: new THREE.Vector3(-1.5, 0, -3.5), r: 1.8, label: '🏋️ lift some weights', workout: 'dumbbells' },
      { pos: new THREE.Vector3(1.8, 0, -3.3), r: 1.9, label: '🛋️ bench press', workout: 'bench' },
      { pos: new THREE.Vector3(6.0, 0, 1.5), r: 1.8, label: '🥊 hit the heavy bag', workout: 'bag' },
      { pos: new THREE.Vector3(4.4, 0, -1.5), r: 1.8, label: '🧘 stretch on the mat', workout: 'yoga' },
      { pos: new THREE.Vector3(0, 0, -5.2), r: 2.4, label: '📋 check the future-sports board', say: 'coming soon to the Rec Center: tennis, soccer, boxing & more!' },
      { pos: this.coach.root.position.clone(), r: 2.4, label: '🗣 talk to Coach Crust', action: () => this._talkToCoach() },
      { pos: new THREE.Vector3(0, 0, 5.4), r: 2.2, label: '← back to the court', action: () => this.manager.goTo('court', { from: 'reccenter' }) }
    ];
    this.nearSpot = null;
  }

  _buildRoom(){
    this.scene.add(new THREE.AmbientLight(0xFFFFFF, 0.85));
    const key = new THREE.DirectionalLight(0xFFF6E8, 0.55);
    key.position.set(4, 10, 6); key.castShadow = true; key.shadow.mapSize.set(1024, 1024);
    this.scene.add(key);

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(14, 14), new THREE.MeshLambertMaterial({ color: 0xB9946A }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true;
    this.scene.add(floor);
    const back = new THREE.Mesh(new THREE.PlaneGeometry(14, 8), new THREE.MeshLambertMaterial({ color: C.sprout }));
    back.position.set(0, 4, -6); back.receiveShadow = true; this.scene.add(back);
    const sideL = back.clone(); sideL.rotation.y = Math.PI/2; sideL.position.set(-7, 4, 0); this.scene.add(sideL);
    const sideR = back.clone(); sideR.rotation.y = -Math.PI/2; sideR.position.set(7, 4, 0); this.scene.add(sideR);

    // exit door back to the court
    this.exitDoor = box(1.5, 2.4, 0.3, C.crustDark, 0, 1.2, 5.9, this.scene);
    const sign = textSprite('← to the Court', 26, '#6B4A2F', 2.8, 0.8);
    sign.position.set(0, 2.9, 5.7); this.scene.add(sign);
  }

  _buildEquipment(){
    // treadmill
    const tm = new THREE.Group(); tm.position.set(-4.5, 0, -2); this.scene.add(tm);
    box(1.0, 0.25, 2.0, 0x3A3A44, 0, 0.15, 0, tm);           // deck
    box(0.9, 0.04, 1.7, 0x1A1A22, 0, 0.29, 0.1, tm);         // belt
    cyl(0.05, 0.05, 1.1, 0x555555, -0.4, 0.85, -0.9, tm, 6);
    cyl(0.05, 0.05, 1.1, 0x555555, 0.4, 0.85, -0.9, tm, 6);
    box(0.9, 0.5, 0.1, 0x222222, 0, 1.4, -0.9, tm);             // console

    // dumbbell rack
    const dr = new THREE.Group(); dr.position.set(-1.5, 0, -3.8); this.scene.add(dr);
    box(1.6, 0.1, 0.5, C.crustDark, 0, 0.7, 0, dr);
    box(1.6, 0.1, 0.5, C.crustDark, 0, 0.35, 0.15, dr);
    for (let i = -2; i <= 2; i++){
      const x = i * 0.32;
      cyl(0.05, 0.05, 0.34, 0x333333, x, 0.78, 0, dr, 6).rotation.z = Math.PI/2;
      sphere(0.1, C.cocoa, x - 0.18, 0.78, 0, dr, 8); sphere(0.1, C.cocoa, x + 0.18, 0.78, 0, dr, 8);
    }

    // bench press
    const bp = new THREE.Group(); bp.position.set(1.8, 0, -3.6); this.scene.add(bp);
    box(0.5, 0.15, 1.6, C.raspberry, 0, 0.55, 0, bp);        // bench pad
    box(0.5, 0.55, 0.15, C.raspberry, 0, 0.3, 0.7, bp);      // incline
    cyl(0.06, 0.06, 1.2, 0x444444, -0.5, 0.9, -0.6, bp, 6); cyl(0.06, 0.06, 1.2, 0x444444, 0.5, 0.9, -0.6, bp, 6);
    const bar = cyl(0.04, 0.04, 1.7, 0x777777, 0, 1.5, -0.6, bp, 8); bar.rotation.z = Math.PI/2;
    sphere(0.22, 0x333333, -0.75, 1.5, -0.6, bp, 10); sphere(0.22, 0x333333, 0.75, 1.5, -0.6, bp, 10);

    // yoga mat
    const mat = box(1.0, 0.05, 2.2, C.cobalt, 4.4, 0.03, -1.5, this.scene);
    mat.rotation.y = 0.2;

    // a heavy bag hanging from the side
    cyl(0.28, 0.24, 1.4, C.rye, 6.0, 1.4, 1.5, this.scene, 12);
    cyl(0.03, 0.03, 0.6, 0x333333, 6.0, 2.4, 1.5, this.scene, 6);
  }

  _buildFutureWall(){
    // reserved slots for the sports that come next — signposts on the back wall
    const sports = ['🎾 TENNIS', '⚽ SOCCER', '🥊 BOXING', '🏊 POOL'];
    sports.forEach((s, i) => {
      const x = -4.5 + i * 3.0;
      box(2.4, 1.4, 0.15, C.sproutDim, x, 2.4, -5.85, this.scene);
      const label = textSprite(s, 22, '#FFF4DF', 2.2, 0.7);
      label.position.set(x, 2.7, -5.7); this.scene.add(label);
      const soon = textSprite('coming soon', 18, '#EAF6E4', 1.8, 0.5);
      soon.position.set(x, 2.1, -5.7); this.scene.add(soon);
    });
    const header = textSprite('FUTURE SPORTS', 30, '#FFF4DF', 4.0, 1.0);
    header.position.set(0, 3.7, -5.7); this.scene.add(header);
  }

  _makeCoach(){
    const build = (g) => {
      breadSlice(g, C.crust, C.toast, 0.7, 1.15);          // bulkier bread slice
      sphere(.07, 0x2E2018, -.18, 1.22, .32, g, 8);
      sphere(.07, 0x2E2018,  .18, 1.22, .32, g, 8);
      // sweatband
      const band = new THREE.Group(); band.position.set(0, 1.78, 0); g.add(band);
      box(.92, .12, .42, 0xE23B4E, 0, 0, 0, band);
      // whistle + lanyard
      const lanyard = new THREE.Group(); lanyard.position.set(0, 1.25, .36); g.add(lanyard);
      cyl(.015, .015, .24, 0x333333, 0, -.12, 0, lanyard, 6);
      sphere(.06, 0xC0C0C0, 0, -.26, 0, lanyard, 8);
      // tank top + muscles
      box(.78, .62, .42, 0x3A5FC0, 0, .55, 0, g);
      cyl(.18, .16, .38, C.toast, -.52, .72, .12, g, 8);  // bicep
      cyl(.18, .16, .38, C.toast,  .52, .72, .12, g, 8);
      stickLegs(g, .55);
    };
    return new Townsperson({
      name: 'Coach Crust', line: 'no pain, no grain!',
      pos: new THREE.Vector3(4.8, 0, 2.2), facing: -Math.PI * .65,
      wander: 0, speed: 0, bubbleY: 2.6, build
    });
  }

  // ---- coach quest dialog ----
  _talkToCoach(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.coach.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z);
    this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    this._renderCoachDialog();
  }

  _renderCoachDialog(){
    this._clearPanels();
    const { available, active } = questsForGiver('coach');
    const questRows = [];
    active.forEach(id => {
      const q = QUESTS[id];
      if (isQuestReady(id)){
        questRows.push(`<div class="q-drow ready"><div><b>${q.title}</b><span>objective complete!</span></div>
          <button class="cafe-btn turnin" data-id="${id}">turn in ✓</button></div>`);
      } else {
        const p = objectiveProgress(id);
        questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${p.have} / ${p.goal} ${q.objective.noun}</span></div><span class="q-dpend">in progress</span></div>`);
      }
    });
    available.forEach(id => {
      const q = QUESTS[id];
      const rew = q.reward?.crumbs ? ` · reward 🍞 ${q.reward.crumbs}` : '';
      questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}${rew}</span></div>
        <button class="cafe-btn accept" data-id="${id}">accept</button></div>`);
    });
    const body = questRows.length ? questRows.join('') : '<p class="q-dnone">"Keep training — I\'ll have a challenge for you soon."</p>';

    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Coach Crust</h2>
        <div class="cafe-sub">avocado rec center</div>
        <div class="q-drows">${body}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); this.bread.drawFace('yum'); this._renderCoachDialog(); });
    p.querySelectorAll('.turnin').forEach(b => b.onclick = () => this._onTurnIn(b.dataset.id));
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _onTurnIn(id){
    const reward = turnIn(id);
    if (!reward) return;
    HUD.refreshCrumbs();
    this.bread.drawFace('yum');
    this._renderCoachDialog();
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.62];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('avocado rec center');
    HUD.say('The Rec Center! Work out on the equipment, talk to Coach Crust for a challenge, and check the board for sports coming soon.');
    HUD.setHint('click to walk · walk up to the gear, Coach Crust, or the exit');
    HUD.hideHintSoon(7000);
  }

  exit(){ this.input.onTap = null; HUD.showEnter(null); HUD.say(''); this._clearPanels(); }

  _panel(html, mode = 'panel'){
    const el = document.createElement('div');
    el.className = mode === 'bare' ? 'cafe-panel bare' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  _startWorkout(key){
    const cfg = this.workouts[key];
    if (!cfg) return;
    this.bread.stop();
    this.bread.startPose(cfg.pose);
    this.bread.drawFace('effort');
    this.workout = { key, cfg, time: cfg.duration, progress: 0, done: false };
    this.input.onTap = null;
    this._renderWorkoutPanel();
  }

  _renderWorkoutPanel(){
    this._clearPanels();
    const { cfg, time, progress } = this.workout;
    const pct = Math.round((progress / cfg.clicks) * 100);
    const timePct = Math.max(0, (time / cfg.duration) * 100);
    const p = this._panel(`
      <div class="cafe-card">
        <h2>${cfg.title}</h2>
        <div class="cafe-sub">tap fast to finish the set before time runs out</div>
        <div class="workout-bar"><span style="width:${pct}%"></span></div>
        <div class="workout-time"><span style="width:${timePct}%"></span></div>
        <button class="cafe-btn workout-go">${cfg.pose === 'run' ? '🏃 stride' : cfg.pose === 'meditate' ? '🧘 breathe' : '💪 push'}</button>
      </div>`);
    const btn = p.querySelector('.workout-go');
    btn.onpointerdown = (e) => { e.preventDefault(); this._workoutPush(); };
    btn.onclick = () => this._workoutPush();
  }

  _workoutPush(){
    if (!this.workout || this.workout.done) return;
    this.workout.progress++;
    this._renderWorkoutPanel();
    if (this.workout.progress >= this.workout.cfg.clicks) this._finishWorkout(true);
  }

  _finishWorkout(success){
    if (!this.workout || this.workout.done) return;
    this.workout.done = true;
    const { cfg } = this.workout;
    this.bread.stopPose();
    this._clearPanels();
    if (success){
      const gained = trainStat(cfg.stat, 1);
      const crumbs = 2 + Math.floor(Math.random() * 3);
      addCrumbs(crumbs);
      HUD.refreshStats();
      HUD.refreshCrumbs();
      this.bread.drawFace('yum');
      HUD.say(cfg.msg + ` (+${gained} ${cfg.stat.toUpperCase()}, +${crumbs} crumbs)`);
      const key = `used${cfg.stat.toUpperCase()}${cfg.pose === 'run' ? 'Run' : cfg.pose === 'meditate' ? 'Zen' : 'Lift'}`;
      if (!getFlag(key)){ setFlag(key, true); questProgress('recUse', 1); }
    } else {
      HUD.say('Toast ran out of steam. Try again after a breather!');
      this.bread.drawFace('o');
    }
    this.workout = null;
    this.input.onTap = (ndc, ray) => this._tap(ray);
  }

  _tap(ray){
    if (this.workout) return;
    if (checkNpcClick(ray, this.coach, this.bread)) return;

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-6, Math.min(6, gp.x));
    gp.z = Math.max(-5, Math.min(5.5, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  update(dt){
    this.bread.update(dt);
    this.coach.update(dt, this.bread.root.position);

    if (this.workout){
      this.workout.time -= dt;
      if (this.workout.time <= 0) this._finishWorkout(false);
    }

    // nearest hotspot → enter prompt
    let near = null, nd = Infinity;
    this.hotspots.forEach(h => {
      if (h.interactive === false || h.hidden) return;
      const d = h.pos.distanceTo(this.bread.root.position);
      if (d < nd){ nd = d; near = h; }
    });
    if (near && nd < near.r){
      if (this.nearSpot !== near){
        this.nearSpot = near;
        HUD.showEnter(near.label, () => {
          if (near.action) near.action();
          else if (near.workout) this._startWorkout(near.workout);
          else { HUD.say(near.say); this.bread.drawFace('effort'); }
        });
      }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    // orbit camera with a gentle follow
    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.4, 1.6, this.bread.root.position.z * 0.4), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
